import { Dispatch, SetStateAction, useEffect } from 'react';

import { AutoFillOptionType, Order, OrderApi, OrderInventory } from 'apis/OrderApi';
import type { AutoFillOption } from 'pages/partners/components/OrdersTab/OrdersTab';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { GMetrixDatePicker } from 'components/GMetrixDatePicker/GMetrixDatePicker';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { TileContainer } from 'components/TileContainer/TileContainer';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { PartnerDetails } from 'apis/PartnerApi';
import { Col, Form, Row } from 'react-bootstrap';
import { License } from 'apis/LicenseApi';
import { getFormattedDate } from 'utils';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

import 'pages/partners/components/OrdersTab/components/OrderFillForm/styles/order-fill-form.scss';

interface OrderFillFormProps {
	details: PartnerDetails;
	getAllOrdersFiltered: (placeNewOrder?: boolean) => void
	orderableLicenses: License[];
	orderAutoFillOptions: AutoFillOption[];
	orderInventory: OrderInventory[];
	page: { selectedOrder: Order | undefined; type: 'Renew' | 'Place' };
    setOrderAutoFillOptions: Dispatch<SetStateAction<AutoFillOption[]>>;
}

export const OrderFillForm = (props: OrderFillFormProps) => {

    const { details, getAllOrdersFiltered, orderableLicenses, orderAutoFillOptions, orderInventory, page, setOrderAutoFillOptions } = props;

    const [state, setState] = useSuperState({
        checkedValue: 0,
        comment: '',
        purchaseOrder: '',
        quantity: page.type === 'Renew' && page.selectedOrder?.NumRequested ? page.selectedOrder.NumRequested.toString() : '1',
        selectedLicense: (page.type === 'Renew' && page.selectedOrder && page.selectedOrder.License ? { Id: page.selectedOrder?.License.Id, Name: page.selectedOrder.License.Name } : undefined) as undefined | { Id: number; Name: string },
        startDate: (page.type === 'Renew' && page.selectedOrder ? { date: new Date(page.selectedOrder?.DateExpire), dateString: page.selectedOrder?.DateExpire.toString() } : { date: undefined, dateString: '' }) as {
		date: Date | undefined;
		dateString: string;
	}
    });
    const { checkedValue, comment, purchaseOrder, quantity, selectedLicense, startDate } = state;

    const { highestRole } = useApp();
    const { formatMessage } = useIntl();
    const { catchWithToast, createToast } = useNotification();
    const defaultLicense = { Id: 0, Name: formatMessage({ id: 'choose_one.e57d2911', defaultMessage: 'Choose one', description: 'formulario' }) };

    const validateAutoFill = () => {

        const supplierAllowed = validateSupplier();
        const copyOfAutoFillOptions = [...orderAutoFillOptions];
        copyOfAutoFillOptions[1].disabled = false;
        copyOfAutoFillOptions[2].disabled = false;
        copyOfAutoFillOptions[1].label = formatMessage({ id: 'from_inventory.55842df5', defaultMessage: 'From Inventory' });

        if (supplierAllowed) {

            copyOfAutoFillOptions[1].disabled = false;
            copyOfAutoFillOptions[2].disabled = false;
            const inventoryAllowed = validateInventory();

            if (inventoryAllowed.valid) {

                copyOfAutoFillOptions[1].disabled = false;
                copyOfAutoFillOptions[1].label = `${formatMessage({ id: 'from_inventory.55842df5', defaultMessage: 'From Inventory' })} (${inventoryAllowed.amount})`;

            } else {

                copyOfAutoFillOptions[1].disabled = true;

            }

        } else {

            copyOfAutoFillOptions[1].disabled = true;
            copyOfAutoFillOptions[2].disabled = true;

        }
        setOrderAutoFillOptions(copyOfAutoFillOptions);

    };

    const validateInventory = () => {

        const inventoryIsValid = { amount: 0, valid: false };
        orderInventory.forEach(obj => {

            if (selectedLicense && selectedLicense.Id === obj.Id) {

                if (obj.Inventory >= parseInt(quantity)) {

                    inventoryIsValid.amount = obj.Inventory;
                    inventoryIsValid.valid = true;

                }

            }

        });
        return inventoryIsValid;

    };

    const validateSupplier = () => {

        let supplierIsValid = false;
        orderableLicenses.forEach(license => {

            if (selectedLicense && license.Id === selectedLicense.Id && highestRole.partnerId === license.SuppliedBy) {

                supplierIsValid = true;

            }

        });
        return supplierIsValid;

    };

    const placeAnOrderClick = async (
        partnerId: number,
        licenseId: number,
        numRequested: number,
        isAutoFill: AutoFillOptionType,
        orderIdToAutoFill: number,
        startDate: Date,
        purchaseOrder: string,
        comment: string,
        isOffline: boolean,
        renewId: number
    ) => {

        try {

            const response = await OrderApi.PlaceOrder(
                partnerId,
                licenseId,
                numRequested,
                isAutoFill,
                orderIdToAutoFill,
                startDate,
                purchaseOrder,
                comment,
                isOffline,
                renewId
            );
            if (response.Done) {

                createToast(page.type === 'Place' ? formatMessage({ id: 'successfully_placed_the_order.d38dc746', defaultMessage: 'Successfully placed the order.' }) : formatMessage({ id: 'successfully_renewed_the_order.f27ee901', defaultMessage: 'Successfully renewed the order' }), 'success');
                getAllOrdersFiltered(page.type === 'Place' ? true : false);

            } else {

                createToast(response.Message === '' ? 'Error occurred on the server, but no error message was provided' : response.Message, 'danger');

            }

        } catch (e) {

            catchWithToast(formatMessage({ id: 'failed_to_place_order.b0bc7650', defaultMessage: 'Failed to place order.' }))(e);

        }

    };

    useEffect(() => {

        if (page.type === 'Place') {

            validateAutoFill();

        }

    }, [selectedLicense, orderInventory, quantity]);

    return (
        <>
            <TileContainer
                addButton
                addButtonCustom={(
                    <GMetrixButton
                        onClick={() => {

                            if (selectedLicense && startDate.date) {

                                placeAnOrderClick(
                                    details.ID,
                                    selectedLicense?.Id,
                                    parseInt(quantity),
                                    checkedValue,
                                    0,
                                    startDate.date,
                                    purchaseOrder,
                                    comment,
                                    false,
                                    page.type === 'Renew' ? page.selectedOrder ? page.selectedOrder.Id : 0 : 0
                                );

                            }

                        }}
                        className="mt-2"
                        disabled={!selectedLicense?.Id || !startDate.date}
                        variant="primary"
                    >
                        {page.type == 'Place' ? <FormattedMessage defaultMessage="Place Order" id="place_order.0da32b8f" /> : <FormattedMessage defaultMessage="Renew Order" id="renew_order.e768c154" />}
                    </GMetrixButton>
                )}
            >
                <Row>
                    <Col className="mb-3" md={10} xl={3}>
                        <GMetrixDropdown
                            dataItemKey="Id"
                            dataItems={[defaultLicense, ...orderableLicenses]}
                            disabled={page.type === 'Renew'}
                            label={formatMessage({ id: 'license_type.3286e892', defaultMessage: 'License Type', description: 'Formulario' })}
                            onChange={val => setState({ selectedLicense: val })}
                            textField="Name"
                            value={selectedLicense ? selectedLicense : defaultLicense}
                        />
                    </Col>
                    <Col className="mb-3" md={2} xl={2}>
                        <GMetrixInput
                            label={formatMessage({ id: 'quantity.3c513ef2', defaultMessage: 'Quantity' })}
                            min={1}
                            onChange={e => e.target.value === '0' ? setState({ quantity: '1' }) : setState({ quantity: e.target.value })}
                            type="number"
                            value={quantity}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col className="mb-3" md={6} xl={2}>
                        <Form.Label>
                            <FormattedMessage defaultMessage="Autofill Options" id="autofill_options.a4d4bd6d" />
                        </Form.Label>

                        <Form>
                            {orderAutoFillOptions.map(option => (
                                <GMetrixCheckInput
                                    onChange={e => {

                                        setState({ checkedValue: option.value });

                                    }}
                                    checked={checkedValue === option.value}
                                    disabled={page.type === 'Renew'}
                                    key={option.label}
                                    label={option.label}
                                    name={formatMessage({ id: 'autofill_options.a4d4bd6d', defaultMessage: 'Autofill Options' })}
                                    type="radio"
                                />
                            ))}
                        </Form>
                    </Col>
                    <Col className="mb-3" md={6} xl={3}>
                        <div className="mb-3">
                            <GMetrixInput
                                label={formatMessage({ id: 'purchase_order_optional.38e6ecd9', defaultMessage: 'Purchase Order (Optional)' })}
                                onChange={e => setState({ purchaseOrder: e.target.value })}
                                value={purchaseOrder}
                            />
                        </div>
                        <GMetrixDatePicker
                            onChange={(date: any) => {

                                if (date) {

                                    setState({ startDate: {
                                        date: new Date(date),
                                        dateString: getFormattedDate(new Date(date))
                                    } });

                                }

                            }}
                            label={formatMessage({ id: 'start_date.66ba5b72', defaultMessage: 'Start Date' })}
                            min={new Date().toISOString().split('T')[0]}
                            value={(startDate && startDate.date) ?? null}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col className="mb-3" md={6} xl={5}>
                        <GMetrixInput label={formatMessage({ id: 'comment.252dd7a3', defaultMessage: 'Comment', description: 'Formulario' })} onChange={e => setState({ comment: e.target.value as string })} type="textarea" value={comment} />
                    </Col>
                </Row>
            </TileContainer>
        </>
    );

};
